/**
 * Helpers for turning the current builder project into a reusable custom
 * template artifact and metadata record.
 */

import { buildProjectZipBlob } from '../core/save-load.js';
import { slugify } from '../export/generators.js';

export { buildProjectZipBlob };

function pageFilename(page, index) {
  if (index === 0) return 'index.html';
  const slug = slugify(page?.name || `page-${index + 1}`) || `page-${index + 1}`;
  return `${slug}.html`;
}

function sectionId(section, fallback) {
  return section?.sectionId || section?.catalogKey || section?.instanceId || fallback;
}

function sectionLabel(section, fallback) {
  return section?.description || section?.sectionId || section?.catalogKey || fallback;
}

function summarizeSection(section, role, fallback) {
  return {
    role,
    tagName: role === 'footer' ? 'footer' : 'section',
    id: sectionId(section, fallback),
    description: sectionLabel(section, fallback),
    catalogKey: section?.catalogKey || null
  };
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

export function extractTemplateMetadata(state, options = {}) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  const sections = [];

  if (state?.sharedSections?.navigation) {
    sections.push(summarizeSection(state.sharedSections.navigation, 'navigation', 'navigation'));
  }

  for (const page of pages) {
    for (const [index, section] of (page.sections || []).entries()) {
      sections.push(summarizeSection(section, 'content', `section-${index + 1}`));
    }
  }

  if (state?.sharedSections?.footer) {
    sections.push(summarizeSection(state.sharedSections.footer, 'footer', 'footer'));
  }

  const fontFamilies = unique([state?.font || 'Roboto', state?.headingFont]);

  return {
    siteName: options.name || state?.projectName || 'Untitled Template',
    industry: options.industry || 'other',
    totalPages: pages.length,
    pages: pages.map((page, index) => ({
      name: page?.name || `Page ${index + 1}`,
      filename: pageFilename(page, index)
    })),
    sections,
    sectionCount: sections.length,
    contentSectionCount: sections.filter((section) => section.role === 'content').length,
    hasNavigation: Boolean(state?.sharedSections?.navigation),
    hasFooter: Boolean(state?.sharedSections?.footer),
    themeVars: { ...(state?.theme || {}) },
    fontFamilies,
    sourceTemplateDomain: state?.sourceTemplateDomain || null
  };
}
